import { useEffect, useRef, useState } from "react";
import ChapterLessonIndex from "./ChapterLessonIndex";
import { Section } from './ChapterLessonIndex'

import backend, { get_doc_thumb_url } from '@/controllers/backend'
import qrcode from "qrcode-generator";
import { IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonIcon } from '@ionic/react';
import { shareSocialOutline, logoTwitter, mailOutline, logoWhatsapp } from 'ionicons/icons';
import Image from '@/components/Image'
import { useParams } from "react-router-dom";

const UserBook: React.FC<{repo_id: string, id: string}> = () => {
    const startedRef = useRef(false);
    const [toc, setToc] = useState<any>({})
    const [sections, setSections] = useState<Section[]>([]);
    const { doc_id, doc_name } = useParams();

    const later = async () => {
        // start by checking if doc_id is in the old format, and if so, forward
        if (!doc_id.match(/^\d+$/)) {
            const doc = await backend.fetch_legacy_doc(doc_id, doc_name)
            console.log(doc)
            window.location = (new URL(window.location).origin) + `/docs/${doc.id}/${doc.name}`
        }

        const fetchChapter = async (chapter_idx) => {
            // TODO: too slow, switch to cerebras from google
            // NOTE: Cerebras doesn't support json well for gpt oss yet
            const chapter_lessons = await backend.chapter_lessons(doc_id, chapter_idx);

            setSections((sections_data) => {
                const new_sections = [...sections_data]
                new_sections[chapter_idx].items = chapter_lessons.map(({title}) => {return {id: title, title}})
                return new_sections
            })
        }

        const fetchToc = async () => {
            console.log("doc_id", doc_id)
            const toc = await backend.toc(doc_id);
            setToc(toc)

            const sections_data = toc.items.map(({chapter_title}) => {return {title: chapter_title, items: []}})
            setSections(sections_data)

            for (let i = 0; i < toc.items.length; ++i)
                await fetchChapter(i);
        }

        fetchToc();
    }

    const [qrCode, setQrCode] = useState("")

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        later();

        // page qr code
        const qr = qrcode(0, 'L')
        qr.addData(document.location.href)
        //qr.addData("Hello!")
        qr.make()
        setQrCode(qr.createDataURL())
        //document.getElementById('qr-url')!.innerHTML = qr.createImgTag();

    }, [])

    return (<>
        <IonCard className="max-w-4xl mx-auto my-6">
            <IonCardHeader className="text-center">
                <IonCardTitle className="text-2xl md:text-3xl font-bold py-2">
                    {toc.book_title}
                </IonCardTitle>
            </IonCardHeader>
            
            <IonCardContent className="px-6 py-4">
                <div className="description grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                    <div className="book-info">
                        <div className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Author{toc.authors && toc.authors.length > 1 ? 's' : ''}: {toc.authors?.join(', ')}
                        </div>
                        <div className="text-md text-gray-600 dark:text-gray-400 mb-4">
                            Published: {toc.year}
                        </div>
                        <div className="text-gray-800 dark:text-gray-200 mb-4">
                            {toc.short_description}
                        </div>
                        <div className="max-w-md m-auto">
                           <Image src={get_doc_thumb_url(doc_id)} className="" />
                        </div>
                        <div className="flex items-center mt-4">
                            <IonIcon icon={shareSocialOutline} className="text-xl mr-2 text-blue-600 dark:text-blue-400" />
                            <span className="font-medium text-gray-700 dark:text-gray-300">Share this book:</span>
                        </div>
                        <div className="flex space-x-4 mt-2">
                            <a 
                                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(document.location.href)}&text=${encodeURIComponent(`Check out this book: ${toc.book_title}`)}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-600 dark:text-blue-300 dark:hover:text-blue-200"
                            >
                                <IonIcon icon={logoTwitter} className="text-2xl" />
                            </a>
                            <a 
                                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out this book: ${toc.book_title} ${document.location.href}`)}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                            >
                                <IonIcon icon={logoWhatsapp} className="text-2xl" />
                            </a>
                            <a 
                                href={`mailto:?subject=${encodeURIComponent(`Check out this book: ${toc.book_title}`)}&body=${encodeURIComponent(`I thought you might be interested in this book: ${document.location.href}`)}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                            >
                                <IonIcon icon={mailOutline} className="text-2xl" />
                            </a>
                            <button 
                                onClick={() => {
                                    if (navigator.share) {
                                        navigator.share({
                                            title: toc.book_title,
                                            text: toc.short_description,
                                            url: document.location.href,
                                        }).catch((error) => console.log('Sharing failed:', error));
                                    }
                                }}
                                className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                            >
                                <IonIcon icon={shareSocialOutline} className="text-2xl" />
                            </button>
                        </div>
                    </div>
                    
                    <div className="qr-code-container flex flex-col items-center">
                        <img 
                            style={{ imageRendering: 'pixelated' }} 
                            src={qrCode} 
                            width="256" 
                            height="256" 
                            className="border-4 border-blue-500 dark:border-blue-400 rounded-xl p-2 bg-white" 
                            alt="QR Code for sharing this book"
                        />
                        <p className="pt-2 text-sm text-gray-600 dark:text-gray-400 text-center">
                            Scan this QR code to open these lessons on your phone
                        </p>
                    </div>
                </div>
            </IonCardContent>
        </IonCard>
        <ChapterLessonIndex sections={sections}/>
    </>)
}

export default UserBook
